import api from "./index";

// Property Trash APIs (admin only)
export const getTrashedProperties = () =>
  api.get("property-management/properties/trash/");

export const restoreProperty = (id) =>
  api.post(`property-management/properties/trash/${id}/restore/`);

// Duplicate Detection APIs
export const findDuplicateProperties = () =>
  api.get("property-management/duplicates/properties/");

export const findDuplicateContacts = () =>
  api.get("property-management/duplicates/contacts/");

// Buyer Matching APIs
export const getMatchedBuyersForProperty = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/matched-buyers/`);

export const getMatchedBuyersForREAProperty = (reaPropertyId) =>
  api.get(`property-management/rea-properties/${reaPropertyId}/matched-buyers/`);

// Inspection Statistics APIs
export const getInspectionStatistics = () =>
  api.get("property-management/inspections/statistics/");

export const getAgentInspectionStatistics = (agentId = null) => {
  if (agentId) {
    return api.get(`property-management/inspections/statistics/agent/${agentId}/`);
  }
  return api.get("property-management/inspections/statistics/agent/");
};

// Property Activity Log API — full audit trail of changes on a property
// (price/status/agent/field edits, publish actions, REA sync outcomes, ...).
export const getPropertyActivityLog = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/activity-log/`);

// Property Interactions APIs (Buyer Portal submissions)
export const getPropertyEnquiries = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/enquiries/`);

// Log an enquiry the agent took by phone/in person directly against a
// property (no buyer-portal submission involved).
export const createPropertyEnquiry = (propertyId, data) =>
  api.post(`property-management/properties/${propertyId}/enquiries/`, data);

export const updateEnquiry = (enquiryId, data) =>
  api.patch(`property-management/enquiries/${enquiryId}/`, data);

export const getPropertyBidderRegistrations = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/bidder-registrations/`);

export const updateBidderRegistration = (registrationId, data) =>
  api.patch(`property-management/bidder-registrations/${registrationId}/`, data);

export const getPropertyOffers = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/offers/`);

export const updateOffer = (offerId, data) =>
  api.patch(`property-management/offers/${offerId}/`, data);

// Phase 1 — explicit "park before send" actions on the property Offers tab.
// These replace the previous behaviour where editing an offer's status would
// auto-email the buyer; nothing reaches the buyer or vendor until the agent
// invokes one of these endpoints.
export const sendOfferToVendor = (offerId, message = "") =>
  api.post(`property-management/offers/${offerId}/send-to-vendor/`, { message });

export const sendOfferToBuyer = (offerId, note = "") =>
  api.post(`property-management/offers/${offerId}/send-to-buyer/`, { note });

// Agent makes an explicit counter offer back to the buyer — editable price + terms.
// payload: { counter_amount, deposit_amount?, settlement_days?, custom_settlement_date?, conditions?, note? }
export const counterOfferToBuyer = (offerId, payload = {}) =>
  api.post(`property-management/offers/${offerId}/counter-to-buyer/`, payload);

// Phase 5 — agent confirms mutual acceptance once both sides have agreed.
// Moves the offer to ACCEPTED, the property to "Under Contract", and expires
// any other open offers on the listing. Pass `force: true` if the agent needs
// to override (e.g. signed paper contract before vendor clicked anything).
export const confirmMutualAcceptance = (offerId, { note = "", force = false } = {}) =>
  api.post(
    `property-management/offers/${offerId}/confirm-mutual-acceptance/`,
    { note, force },
  );

export const getOfferHistory = (offerId) =>
  api.get(`property-management/offers/${offerId}/history/`);

// Vendor Portal — invite lifecycle on a property
export const getPropertyVendorStatus = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/vendor-status/`);

export const resendPropertyVendorInvite = (propertyId) =>
  api.post(`property-management/properties/${propertyId}/resend-vendor-invite/`);

// Post-sale "claim your property" invite. Body is optional — with no email the
// backend resolves the buyer from the accepted offer / purchaser fields; pass
// { email, first_name, last_name, mobile_number } to send to a specific buyer.
export const sendPropertyClaimInvite = (propertyId, payload = {}) =>
  api.post(`property-management/properties/${propertyId}/send-claim-invite/`, payload);

export const getPropertyInteractionsSummary = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/interactions-summary/`);

// Property "Buyers" tab — agent-curated buyers attached to a listing.
// Each buyer is a CRM Contact; notes/reminders live on the contact profile.
export const getPropertyBuyers = (propertyId) =>
  api.get(`property-management/properties/${propertyId}/buyers/`);

// data: { contact_id } to attach an existing contact, OR { name, email, phone } to create + attach.
// Optional { notes } stored on the link.
export const addPropertyBuyer = (propertyId, data) =>
  api.post(`property-management/properties/${propertyId}/buyers/`, data);

// Attach MANY buyers in one action — the "we had 30 buyers through this listing"
// case (Monday DOMA 12840979019). Every attached buyer also has this listing's
// specs folded into their buying preferences unless seed_preferences is false,
// which is what puts them in Buyer Match for this and similar properties.
//   data: { contact_ids?: [id], people?: [{name,email,phone}], paste?: string,
//           notes?: string, seed_preferences?: boolean }
// → { detail, attached, already_attached, created_contacts, seeded, skipped[], results[] }
export const addPropertyBuyersBulk = (propertyId, data) =>
  api.post(`property-management/properties/${propertyId}/buyers/bulk/`, data);

// Edit an attached buyer — link `notes` and/or the contact's name/email/phone.
export const updatePropertyBuyer = (propertyId, linkId, data) =>
  api.patch(`property-management/properties/${propertyId}/buyers/${linkId}/`, data);

export const removePropertyBuyer = (propertyId, linkId) =>
  api.delete(`property-management/properties/${propertyId}/buyers/${linkId}/`);

// Property-scoped Bulk Communicator (Enquiries & Buyers tabs). Unlike the
// inspection version these aren't tied to an OFI; the {link} token resolves to
// the property's public portal listing link.
//   recipients: [{ id, name, phone, contact_id?, email? }]
// → { messages: [{ attendee_id, name, phone, body }], skipped, agent_name, address }
export const getPropertyBulkSmsPayload = (propertyId, payload = {}) =>
  api.post(`property-management/properties/${propertyId}/bulk-sms-payload/`, payload);

// Best-effort log that the agent texted these recipients from their own device.
//   recipients: [{ id, contact_id?, email?, name? }]
export const markPropertyBulkSmsHandedOff = (propertyId, recipients) =>
  api.post(`property-management/properties/${propertyId}/mark-sms-handed-off/`, { recipients });

// Email channel of the property Bulk Communicator — renders per-recipient
// subject + body for the agent's own mail app (mailto:), from the agent's
// personal address. {link} resolves to the property's public portal listing.
//   recipients: [{ id, name, email, contact_id?, phone? }]
// → { messages: [{ attendee_id, name, email, subject, body }], skipped, agent_name, address }
export const getPropertyBulkEmailPayload = (propertyId, payload = {}) =>
  api.post(`property-management/properties/${propertyId}/bulk-email-payload/`, payload);

// Best-effort log that the agent emailed these recipients from their own mail app.
//   recipients: [{ id, contact_id?, email?, name? }]
export const markPropertyBulkEmailHandedOff = (propertyId, recipients) =>
  api.post(`property-management/properties/${propertyId}/mark-email-handed-off/`, { recipients });

// Email the property's Contract of Sale to a buyer attached to this listing.
export const sendBuyerContractOfSale = (propertyId, linkId) =>
  api.post(`property-management/properties/${propertyId}/buyers/${linkId}/send-contract/`);

